import { Helmet } from 'react-helmet';
import React from 'react';
import dayjs from 'dayjs';
import {
    useGlobalLocalization,
    usePageLocalization,
} from '@sbi-international/boutik.gatsby.plugin.multilocale';
import { useTranslation } from 'react-i18next';
import { useCookiesConsent } from '@sbi-international/boutik.package.cookie.consent';

interface ISeo {
    title: string | undefined | null;
    description: string | undefined | null;
    isDescriptionCurrentLocale: boolean | undefined;
    isNoRobot?: boolean;
    type?: string;
    schema?: {
        product: any;
        price: any;
        url: any;
        reviews: any;
    };
}

export default function SEO({
    title,
    description,
    isDescriptionCurrentLocale,
    isNoRobot,
    schema,
}: ISeo) {
    const { localizationContext } = useGlobalLocalization();
    const { currentPageAlternatives, currentLocale } = usePageLocalization();
    const { t } = useTranslation('layout');
    const { activateKlaviyoCookies } = useCookiesConsent();

    /* Schema related */
    const { reviews } = schema || {};

    const isDev =
        process.env.GATSBY_BUILD_TYPE === 'dev' ||
        process.env.GATSBY_BUILD_TYPE === 'staging';

    let reviewsAverage = 0;
    let reviewsCount = 0;
    if (reviews) {
        reviews.forEach((review: { average_rating: number }) => {
            reviewsCount = reviewsCount + 1;
            reviewsAverage = reviewsAverage + review.average_rating;
        });
        reviewsAverage = (reviewsAverage / reviewsCount) * 0.05;
    }

    if (isDescriptionCurrentLocale && currentLocale.includes('en_')) {
        description = `${description}${
            currentLocale == 'en_CA' ? ' Can.' : ' Us.'
        }`;
    }

    const websiteUrl = process.env.GATSBY_BASE;

    return (
        <Helmet>
            <html lang={localizationContext.currentLocale.bcp47LocaleCode} />

            <title>{`${title} | ${t('seo.mfp')}`}</title>
            {(isDev || isNoRobot) && (
                <meta name='robots' content='noindex, nofollow' />
            )}
            <meta name='description' content={description as string} />
            <meta property='og:title' content={title as string} />
            <meta property='og:description' content={description as string} />
            <meta property='og:type' content='website' />

            {process.env.GATSBY_GOOGLE_SITE_VERIFICATION && (
                <meta
                    name='google-site-verification'
                    content={process.env.GATSBY_GOOGLE_SITE_VERIFICATION}
                />
            )}
            <link
                rel='canonical'
                href={
                    websiteUrl +
                    currentPageAlternatives[
                        localizationContext.currentLocale.localeCode
                    ]
                }
            />
            {Object.keys(currentPageAlternatives).map((key) => {
                return (
                    <link
                        rel='alternate'
                        href={websiteUrl + currentPageAlternatives[key]}
                        hrefLang={
                            localizationContext.locales[key].bcp47LocaleCode
                        }
                        key={key}
                    />
                );
            })}
            {/* la langue par default est fr-ca */}
            <link
                rel='alternate'
                href={websiteUrl + '/ca/fr/'}
                hrefLang={'x-default'}
            />

            {activateKlaviyoCookies && (
                <script
                    async
                    type='text/javascript'
                    src='//static.klaviyo.com/onsite/js/klaviyo.js?company_id=RQyJdW'
                />
            )}
        </Helmet>
    );
}
